import { ICustomerCRUD } from '@monofront/business/interfaces';
import { ICustomer } from '@monofront/types';
import logger from '@utils/logger';
import { klaviyoIdentify } from '@utils/tracking';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  DocumentReference,
  Query,
} from '@firebase/firestore';
import firebaseService from '../firebase';

const CUSTOMER_DB_COLLECTION = 'customers';

const isUpdateIdenticalToCustomer = (
  customer: ICustomer | undefined,
  uid: string,
  data: Partial<ICustomer>,
) => {
  if (!customer || customer.uid !== uid) {
    logger.warn('Trying to update a customer document with another user');

    return false;
  }

  return Object.entries(data).every(([key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify(customer[key])) {
      return false;
    }

    return true;
  });
};

export class CustomerCRUD implements ICustomerCRUD {
  private customer: ICustomer | undefined;

  public listen(uid, callback) {
    const docRef = doc(firebaseService.db, CUSTOMER_DB_COLLECTION, uid);

    return onSnapshot(docRef, (snapshot) => {
      const newCustomer = snapshot.data() as ICustomer;

      if (newCustomer && newCustomer.uid !== this.customer?.uid) {
        klaviyoIdentify({
          $email: newCustomer.email,
        });
      }

      if (newCustomer) {
        this.customer = newCustomer;
        callback(newCustomer);
      }
    });
  }
  public update(uid: string, data: Partial<ICustomer>) {
    if (isUpdateIdenticalToCustomer(this.customer, uid, data)) {
      logger.info('Prevented an identical update to customer document', {
        uid,
        data,
      });

      return Promise.resolve(this.customer);
    }

    const docRef = doc(firebaseService.db, CUSTOMER_DB_COLLECTION, uid);

    return updateDoc(docRef, data);
  }
  public async read(uid, options) {
    const docRef = doc(firebaseService.db, CUSTOMER_DB_COLLECTION, uid);
    let docOrQuery: DocumentReference | Query = docRef;

    if (options.collection) {
      const collectionRef = collection(docRef, options.collection);
      let q: Query = collectionRef;

      if (options.orderBy) {
        const orderByConstraints = options.orderBy.map(({ direction, value }) =>
          orderBy(value, direction),
        );

        q = query(collectionRef, ...orderByConstraints);
      }

      if (options.limit) {
        const constraints = options.orderBy
          ? [
              ...options.orderBy.map(({ direction, value }) =>
                orderBy(value, direction),
              ),
              limit(options.limit),
            ]
          : [limit(options.limit)];

        q = query(collectionRef, ...constraints);
      }

      docOrQuery = q;
    }

    if (docOrQuery instanceof DocumentReference) {
      const snapshot = await getDoc(docOrQuery);

      return snapshot.data();
    } else {
      const snapshot = await getDocs(docOrQuery);

      return snapshot.docs.map((doc) => doc.data());
    }
  }
}
